/**
 * AskBox.tsx — the one-shot query box (visible via the ⚡ toolbar button or
 * Cmd/Ctrl+K).
 *
 * A small portaled input that opens near the cursor. The user types a query,
 * the box POSTs to /api/projects/{id}/ask (one-shot; the agent may inspect the
 * project via tools but the prompt constrains the answer to insert-ready text),
 * and on response calls `target.insert(text)` — for Monaco that replaces the
 * selection; for a text input it's setRangeText.
 *
 * With NO insert target focused (opened from the toolbar, no editor), the
 * answer is NOT discarded: it renders inside the box with a Copy button —
 * the ask box doubles as a quick "ask, read, copy" surface.
 *
 * Extensible: any surface that registers an InsertTarget (registry.ts) gets the
 * box. Phase 1 ships Monaco-only; dashboard inputs can register later with no
 * AskBox change.
 *
 * The box is always mounted when canRemote (cheap); it shows/hides on the
 * shortcut/button + Esc. The active target + selection are captured when the
 * box OPENS (so the user can move the cursor while typing without losing the
 * insertion point).
 */
import { useEffect, useRef, useState } from 'react';
import { Check, Copy } from '@phosphor-icons/react';
import { readChatSse } from '../chat/chatRuntime';
import { activeInsertTarget, type CursorRect, type InsertSelection } from './registry';

export interface AskBoxProps {
  open: boolean;
  onClose: () => void;
  backendOrigin: string;
  token: string | null;
  projectId: string;
  provider?: string | null;
  model?: string | null;
}

interface OpenSnapshot {
  targetId: string;
  insert: (text: string) => void;
  selection: InsertSelection | null;
  rect: CursorRect | null;
}

export function AskBox(props: AskBoxProps): JSX.Element | null {
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The no-insert-target answer (rendered inline with Copy instead of lost).
  const [answer, setAnswer] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Capture the target + selection at open time so cursor moves don't lose it.
  const snapshotRef = useRef<OpenSnapshot | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (props.open) {
      const target = activeInsertTarget();
      snapshotRef.current = target
        ? {
            targetId: target.id,
            insert: target.insert,
            selection: target.getSelection(),
            rect: target.getRect(),
          }
        : null;
      setQuery('');
      setError(null);
      setAnswer(null);
      setCopied(false);
      // Focus the input on the next tick (after render).
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [props.open]);

  if (!props.open) return null;

  const snap = snapshotRef.current;
  const sel = snap?.selection ?? null;
  // Position: near the cursor rect if available, else top-center fallback.
  const style: React.CSSProperties = snap?.rect
    ? { left: snap.rect.left, top: snap.rect.bottom + 6 }
    : { left: '50%', top: 80, transform: 'translateX(-50%)' };

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!query.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const url = `${props.backendOrigin}/api/projects/${encodeURIComponent(props.projectId)}/ask`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(props.token ? { Authorization: `Bearer ${props.token}` } : {}),
        },
        body: JSON.stringify({
          query,
          context: {
            selection: sel?.text ?? null,
            file: sel?.file ?? null,
            lineRange: sel?.lines ?? null,
          },
          provider: props.provider ?? null,
          model: props.model ?? null,
        }),
      });
      if (!res.ok || !res.body) {
        const detail = await res.text().catch(() => res.statusText);
        setError(`Error (${res.status}): ${detail}`);
        setBusy(false);
        return;
      }
      // Accumulate text deltas; the ask endpoint yields insert-ready text.
      let text = '';
      for await (const part of readChatSse(res.body)) {
        if (part.type === 'text' && part.delta) text += part.delta;
        else if (part.type === 'error') setError(part.message ?? 'error');
        else if (part.type === 'finish') break;
      }
      if (snap) {
        snap.insert(text);
        props.onClose();
      } else {
        // No editor focused — show the answer here (with Copy) instead of
        // silently dropping it.
        setAnswer(text);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function copyAnswer() {
    if (!answer) return;
    try {
      await navigator.clipboard.writeText(answer);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — the text stays selectable */
    }
  }

  return (
    <div className="askbox-overlay" onClick={props.onClose}>
      <form
        className="askbox"
        style={style}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <div className="askbox-hint">
          {sel?.text
            ? `Replace selection${sel.file ? ` in ${sel.file}` : ''}:`
            : snap
              ? 'Insert at cursor:'
              : 'Ask anything — the answer appears below:'}
        </div>
        {sel?.text && (
          <pre className="askbox-selection">{sel.text.slice(0, 200)}{sel.text.length > 200 ? '…' : ''}</pre>
        )}
        <input
          ref={inputRef}
          className="askbox-input"
          placeholder={snap ? 'Describe what to insert…' : 'Ask about this project…'}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') props.onClose();
          }}
          disabled={busy}
        />
        {error && <div className="askbox-error">{error}</div>}
        {answer !== null && (
          <div className="askbox-answer">
            <pre className="askbox-answer-text">{answer}</pre>
            <button type="button" className="askbox-copy" onClick={copyAnswer}>
              {copied ? <Check size={13} weight="bold" /> : <Copy size={13} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        )}
        <div className="askbox-actions">
          <span className="askbox-shortcut">⌘K to toggle · Esc to close</span>
          <button type="submit" className="askbox-submit" disabled={busy || !query.trim()}>
            {busy ? 'Working…' : snap ? 'Insert' : 'Ask'}
          </button>
        </div>
      </form>
    </div>
  );
}
